import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PaletteMode } from '@mui/material';
import { getItem, setItem } from '../../../io';

export type ColorMode = PaletteMode;

const STORAGE_KEY = 'colorMode';

type ColorModeContextValue = {
  mode: ColorMode;
  toggleColorMode: () => void;
};

const ColorModeContext = React.createContext<ColorModeContextValue>({
  mode: 'light',
  toggleColorMode: () => undefined,
});

function ColorModeProvider({ children }: React.PropsWithChildren<{}>) {
  const [mode, setMode] = useState<ColorMode>('light');

  useEffect(() => {
    const stored = getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
    setMode(stored);
    document.documentElement.dataset.colorMode = stored;
  }, []);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      setItem(STORAGE_KEY, next);
      document.documentElement.dataset.colorMode = next;
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggleColorMode,
    }),
    [mode, toggleColorMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
}

export { ColorModeContext, ColorModeProvider };
